import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Save, X, Upload } from 'lucide-react';
import ComponentIcon from './ComponentIcon';

const ITEM_TYPE = 'COMPONENT';

// Draggable component in list
const DraggableListItem = ({ component }) => {
  const [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: { id: component._id, fromList: true },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  });

  return (
    <div
      ref={drag}
      className={`flex items-center gap-2 p-2 bg-white rounded-lg shadow-sm cursor-move ${isDragging ? 'opacity-50' : ''}`}
    >
      <ComponentIcon type={component.type} number={component.number} size={24} />
      <span className="text-sm text-[#433225] truncate flex-1">{component.name}</span>
    </div>
  );
};

// Draggable component on map
const DraggableMapItem = ({ component, onRemove }) => {
  const [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: { id: component._id, fromList: false, x: component.x, y: component.y },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  });

  return (
    <div
      ref={drag}
      className={`absolute cursor-move ${isDragging ? 'opacity-50' : ''}`}
      style={{
        left: `${component.x}%`,
        top: `${component.y}%`,
        transform: 'translate(-50%, -50%)'
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        onRemove(component._id);
      }}
      title={`${component.name} - Rechtsklick zum Entfernen`}
    >
      <ComponentIcon type={component.type} number={component.number} size={32} />
    </div>
  );
};

// Drop zone (the floorplan image)
const FloorPlanDropZone = ({ imageData, imageType, components, onDrop, onRemove }) => {
  const imageWrapperRef = useRef(null);

  const [, drop] = useDrop({
    accept: ITEM_TYPE,
    drop: (item, monitor) => {
      const offset = monitor.getClientOffset();
      // Use the image wrapper for coordinate calculation
      const imageRect = imageWrapperRef.current.getBoundingClientRect();

      const x = ((offset.x - imageRect.left) / imageRect.width) * 100;
      const y = ((offset.y - imageRect.top) / imageRect.height) * 100;

      onDrop(item.id, Math.max(0, Math.min(100, x)), Math.max(0, Math.min(100, y)));
    }
  });

  drop(imageWrapperRef);

  return (
    <div className="bg-[#f0f0f0] rounded-lg overflow-auto flex justify-center" style={{ minHeight: 300 }}>
      {imageData ? (
        <div ref={imageWrapperRef} className="relative" style={{ width: 'fit-content' }}>
          <img
            src={`data:${imageType};base64,${imageData}`}
            alt="Floorplan"
            className="block max-w-full h-auto pointer-events-none"
            style={{ maxHeight: '50vh' }}
          />
          {components.map(comp => (
            <DraggableMapItem
              key={comp._id}
              component={comp}
              onRemove={onRemove}
            />
          ))}
        </div>
      ) : (
        <div className="text-[#433225]/50 py-20">
          Kein Plan geladen
        </div>
      )}
    </div>
  );
};

const FloorPlanEditorInner = ({ facilityId, floorplanId, onClose }) => {
  const [floorplan, setFloorplan] = useState(null);
  const [allComponents, setAllComponents] = useState([]);
  const [placedComponents, setPlacedComponents] = useState([]);
  const [unplacedComponents, setUnplacedComponents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      if (floorplanId) {
        const fpRes = await fetch(`/api/floorplans/${floorplanId}`);
        const fpData = await fpRes.json();
        setFloorplan(fpData);
      }

      const compRes = await fetch(`/api/facilities/${facilityId}/components`);
      const compData = await compRes.json();
      setAllComponents(compData);

      // Split into placed/unplaced
      const placed = compData.filter(c => c.floorPlanId === floorplanId && c.x !== null);
      const unplaced = compData.filter(c => c.floorPlanId !== floorplanId || c.x === null);
      setPlacedComponents(placed);
      setUnplacedComponents(unplaced);
    };

    fetchData();
  }, [facilityId, floorplanId]);

  const handleDrop = useCallback((componentId, x, y) => {
    // Move from unplaced to placed
    const comp = allComponents.find(c => c._id === componentId);
    if (!comp) return;

    const updatedComp = { ...comp, floorPlanId: floorplanId, x, y };

    setPlacedComponents(prev => {
      const filtered = prev.filter(c => c._id !== componentId);
      return [...filtered, updatedComp];
    });
    setUnplacedComponents(prev => prev.filter(c => c._id !== componentId));

    setPendingChanges(prev => ({
      ...prev,
      [componentId]: { floorPlanId: floorplanId, x, y }
    }));
  }, [allComponents, floorplanId]);

  const handleRemove = useCallback((componentId) => {
    const comp = placedComponents.find(c => c._id === componentId);
    if (!comp) return;

    const updatedComp = { ...comp, floorPlanId: null, x: null, y: null };

    setUnplacedComponents(prev => [...prev, updatedComp]);
    setPlacedComponents(prev => prev.filter(c => c._id !== componentId));

    setPendingChanges(prev => ({
      ...prev,
      [componentId]: { floorPlanId: null, x: null, y: null }
    }));
  }, [placedComponents]);

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const [componentId, changes] of Object.entries(pendingChanges)) {
        await fetch(`/api/components/${componentId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(changes)
        });
      }
      setPendingChanges({});
      onClose();
    } catch (error) {
      console.error('Error saving:', error);
      alert('Fehler beim Speichern');
    }
    setSaving(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', floorplan?.name || 'Plan');

    try {
      await fetch(`/api/floorplans/${floorplanId}`, {
        method: 'PUT',
        body: formData
      });

      // Refetch floorplan
      const fpRes = await fetch(`/api/floorplans/${floorplanId}`);
      const fpData = await fpRes.json();
      setFloorplan(fpData);
    } catch (error) {
      console.error('Error uploading:', error);
    }
  };

  const hasPendingChanges = Object.keys(pendingChanges).length > 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#f7e6d8] rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#e8d5c7]">
          <h2 className="text-xl font-bold text-[#433225]">
            Komponenten platzieren
          </h2>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-[#433225] hover:bg-[#e8d5c7] cursor-pointer">
              <Upload className="w-4 h-4" />
              Plan ersetzen
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={handleFileUpload}
                className="hidden"
              />
            </label>
            <button
              onClick={handleSave}
              disabled={!hasPendingChanges || saving}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                hasPendingChanges
                  ? 'bg-[#e89f71] text-white hover:bg-[#d88f61]'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <Save className="w-4 h-4" />
              {saving ? 'Speichern...' : 'Speichern'}
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-[#e8d5c7] text-[#433225]"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Component list */}
          <div className="w-64 border-r border-[#e8d5c7] p-4 overflow-y-auto">
            <h3 className="text-sm font-semibold text-[#433225] mb-2">
              Nicht platziert ({unplacedComponents.length})
            </h3>
            <div className="space-y-2 mb-6">
              {unplacedComponents.map(comp => (
                <DraggableListItem key={comp._id} component={comp} />
              ))}
            </div>

            <h3 className="text-sm font-semibold text-[#433225] mb-2">
              Platziert ({placedComponents.length})
            </h3>
            <div className="space-y-2 opacity-60">
              {placedComponents.map(comp => (
                <div key={comp._id} className="flex items-center gap-2 p-2 bg-white/50 rounded-lg">
                  <ComponentIcon type={comp.type} number={comp.number} size={24} />
                  <span className="text-sm text-[#433225] truncate flex-1">{comp.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Floorplan */}
          <div className="flex-1 p-4 overflow-auto">
            <FloorPlanDropZone
              imageData={floorplan?.imageData}
              imageType={floorplan?.imageType}
              components={placedComponents}
              onDrop={handleDrop}
              onRemove={handleRemove}
            />
            <p className="text-sm text-[#433225]/60 mt-2 text-center">
              Komponenten per Drag & Drop platzieren. Rechtsklick zum Entfernen.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Wrap with DnD provider
const FloorPlanEditor = (props) => (
  <DndProvider backend={HTML5Backend}>
    <FloorPlanEditorInner {...props} />
  </DndProvider>
);

export default FloorPlanEditor;
